// Moving a cabinet run's CORNERS — making a section longer or shorter.
//
// The user's rule (2026-11-04): drag the corner where two sections meet to
// make one of them longer or shorter. That section's far end stays where it
// is, and everything past the corner slides with it, keeping its own lengths
// and every angle — so a square bend stays square. No bend is added or taken
// away, and no angle changes.
//
// Which way a corner may move is decided by what would slide. Only island
// sections can slide: a wall section is held by its wall. So:
//   - a corner moves along section A (A grows at this end) only when every
//     section after it is an island section; along section B (B grows at its
//     start) only when every section before it is;
//   - where an island section leaves a wall (a peninsula's root) that leaves
//     one way: the root slides along the wall, the island riding with it;
//   - where two wall sections meet it is the room's corner, and stays.
// A corner that cannot move is reported with why, never hidden.
//
// A section's typed length moves the run END it reaches (as the end handle
// does), and otherwise the corner whose far side can slide.
//
// Pure. The edits are STORED runs; the caller re-solves and writes.
import {
  getWallAxes,
  worldToWallParam,
} from '@shared-lib/kitchen-assembly/wallMath';
import { VIRTUAL_END_EPS } from '@shared-lib/kitchen-assembly/cornerDetection';
import {
  isFreeLeg,
  legDirection,
  legEndpoints,
  legLength,
} from '@shared-lib/cabinet-runs/bendGeometry';
import { baysForRun } from '@shared-lib/cabinet-runs/bays';
import { translateFreeSegmentAppliances } from '@shared-lib/cabinet-runs/moveAppliances';
import {
  materializeRunObjects,
  objectsAfterRunResolve,
  rehydrateRun,
} from './cabinetRunGeometry';
import {
  MIN_RUN_SPAN,
  clampRunEnd,
  dropStrandedOverrides,
  runEndEdit,
  runEndSegment,
  runNeighbourSpans,
  segmentFrame,
} from './runDragMath';

/** Why a corner where two wall sections meet stays where it is. */
export const WALLS_MEET_REASON =
  "Two walls meet here, so this corner stays in the room's corner.";

/** Why a corner whose move would drag a wall section along stays. */
export const OFF_WALL_REASON =
  'Moving this corner would pull part of the run off its wall.';

const EPS = 1e-9;
const dist = (p, q) => Math.hypot(p.x - q.x, p.z - q.z);
// `+ 0` turns a -0 into 0: a zero component of a turned vector is otherwise
// a negative zero, which no reader means.
const scaled = (d, k) => ({ x: d.x * k + 0, z: d.z * k + 0 });
const shifted = (p, d) => ({ ...p, x: p.x + d.x, z: p.z + d.z });

/**
 * The run's segments rehydrated in STORED order, or null when rehydrating
 * dropped one (a wall the room no longer has) — then the two numberings part
 * company and no corner can be named safely.
 */
function hydratedSegments(stored, room) {
  const segs = rehydrateRun(stored, room)?.segments || [];
  return segs.length === (stored?.segments || []).length &&
    segs.every((s) => legEndpoints(s))
    ? segs
    : null;
}

/** May every section in `[from, to]` slide? Only island sections can. */
const allFree = (segs, from, to) => {
  for (let k = from; k <= to; k += 1) if (!isFreeLeg(segs[k])) return false;
  return true;
};

/**
 * One corner — where section `i` ends and section `i + 1` starts — with the
 * ways it may move. `axes[].along` names the section that grows; `dir` is the
 * way the corner travels for it to grow.
 */
function cornerAt(segs, i) {
  const a = legEndpoints(segs[i]);
  const b = legEndpoints(segs[i + 1]);
  if (!a || !b || dist(a.end, b.start) > VIRTUAL_END_EPS) return null;
  const axes = [];
  const dirA = legDirection(segs[i]);
  const dirB = legDirection(segs[i + 1]);
  if (dirA && allFree(segs, i + 1, segs.length - 1))
    axes.push({ along: i, dir: dirA });
  if (dirB && allFree(segs, 0, i))
    axes.push({ along: i + 1, dir: scaled(dirB, -1) });
  const bothWalls = !isFreeLeg(segs[i]) && !isFreeLeg(segs[i + 1]);
  return {
    cornerIndex: i,
    point: { x: a.end.x, z: a.end.z },
    axes,
    pinned: axes.length === 0,
    reason: axes.length
      ? null
      : bothWalls
        ? WALLS_MEET_REASON
        : OFF_WALL_REASON,
  };
}

/**
 * Every corner of a run: `{ cornerIndex, point, axes, pinned, reason }`,
 * where corner `i` joins stored sections `i` and `i + 1`. Sections that do not
 * meet (two pieces of one run) have no corner between them.
 */
export function runCornerHandles(stored, room) {
  const segs = hydratedSegments(stored, room);
  return segs ? cornerHandlesOfSegments(segs) : [];
}

/**
 * The same, from segments already rehydrated in stored order — what a drag's
 * preview holds (`previewForRunEdit().run`), so the handles ride the ghost.
 */
export function cornerHandlesOfSegments(segs) {
  const out = [];
  for (let i = 0; i < (segs || []).length - 1; i += 1) {
    const c =
      legEndpoints(segs[i]) && legEndpoints(segs[i + 1])
        ? cornerAt(segs, i)
        : null;
    if (c) out.push(c);
  }
  return out;
}

/**
 * Re-measure a stretched island section's bays from its new origin. An island
 * section's cabinets are measured from its synthesized wall's origin, which
 * either end may be (it follows the cabinets' side); moving that end moves
 * the origin under the bays, so they are shifted by it and stand where they
 * stood — `runEndEdit`'s rule.
 */
function reanchorBays(stored, idx, before, after) {
  const was = segmentFrame(before);
  const now = segmentFrame(after);
  if (!was || !now) return stored;
  const shift = worldToWallParam(now.wall, was.wall.alignment.bottomLeft);
  if (!Number.isFinite(shift) || Math.abs(shift) < EPS) return stored;
  const bays = baysForRun(stored).map((b) =>
    (b.segmentIndex ?? 0) === idx && Number.isFinite(b.anchorParam)
      ? { ...b, anchorParam: b.anchorParam + shift }
      : b
  );
  // expressed in BAYS from here on (`runEndEdit`, `editBayAnchor`)
  const { appliances: _legacy, ...rest } = stored;
  return { ...rest, bays };
}

/**
 * How far the stretched section may actually grow: never shorter than a run
 * can be (`MIN_RUN_SPAN`), and a wall section never past its wall's ends.
 */
function clampAmount(seg, atEnd, amount) {
  let amt = Math.max(amount, MIN_RUN_SPAN - legLength(seg));
  if (!isFreeLeg(seg)) {
    const wallLen = getWallAxes(seg.wall)?.wallLen;
    const sign = Math.sign(seg.endParam - seg.startParam) || 1;
    const param = atEnd
      ? seg.endParam + amt * sign
      : seg.startParam - amt * sign;
    if (Number.isFinite(wallLen)) {
      const held = Math.min(Math.max(param, 0), wallLen);
      amt = atEnd
        ? (held - seg.endParam) * sign
        : (seg.startParam - held) * sign;
    }
  }
  return amt;
}

/**
 * Move corner `cornerIndex` so section `along` grows by `amount` metres
 * (negative shrinks it); what lies past the corner slides with it.
 *
 * @returns {{ stored: Object, moves: {segIndex: number, delta: {x, z}}[],
 *   amount: number } | { refused: string }} `moves` are the island sections
 *   that slid, by how much — their appliances must go with them; `amount` is
 *   what the section actually grew by, after the clamps
 */
export function cornerMoveEdit(stored, room, cornerIndex, along, amount) {
  const segs = hydratedSegments(stored, room);
  if (!segs)
    return { refused: 'This run has a section the plan cannot place.' };
  const corner =
    cornerIndex >= 0 && cornerIndex < segs.length - 1
      ? cornerAt(segs, cornerIndex)
      : null;
  if (!corner) return { refused: 'There is no corner there.' };
  const axis = corner.axes.find((x) => x.along === along);
  if (!axis) return { refused: corner.reason || OFF_WALL_REASON };
  if (!Number.isFinite(amount)) return { refused: 'No distance to move by.' };

  const atEnd = along === cornerIndex;
  const amt = clampAmount(segs[along], atEnd, amount);
  const delta = scaled(axis.dir, amt);
  const carried = atEnd
    ? range(cornerIndex + 1, segs.length - 1)
    : range(0, cornerIndex);

  const before = stored.segments[along];
  let grown;
  if (isFreeLeg(segs[along])) {
    grown = atEnd
      ? { ...before, end: shifted(before.end, delta) }
      : { ...before, start: shifted(before.start, delta) };
  } else {
    const sign = Math.sign(before.endParam - before.startParam) || 1;
    grown = atEnd
      ? { ...before, endParam: before.endParam + amt * sign }
      : { ...before, startParam: before.startParam - amt * sign };
  }
  const segments = stored.segments.map((s, k) => {
    if (k === along) return grown;
    if (carried.includes(k))
      return {
        ...s,
        start: shifted(s.start, delta),
        end: shifted(s.end, delta),
      };
    return s;
  });
  let next = { ...stored, segments };
  if (isFreeLeg(segs[along])) next = reanchorBays(next, along, before, grown);
  else if (amt < 0) next = dropStrandedOverrides(next, along);
  return {
    stored: next,
    moves: carried.map((k) => ({ segIndex: k, delta })),
    amount: amt,
  };
}

function range(from, to) {
  const out = [];
  for (let k = from; k <= to; k += 1) out.push(k);
  return out;
}

/**
 * Every section of a run, its length, and how a typed length reaches it:
 * `{ section, index, lengthM, how, reason }`. `how` is the run END the
 * section reaches (`{ kind: 'end', end }`), else the corner whose far side
 * slides (`{ kind: 'corner', cornerIndex, along }`), else null with `reason`.
 */
export function runSectionLengths(stored, room) {
  const segs = hydratedSegments(stored, room);
  if (!segs) return [];
  const last = segs.length - 1;
  return segs.map((seg, i) => {
    const row = { section: i + 1, index: i, lengthM: legLength(seg) };
    if (i === last)
      return { ...row, how: { kind: 'end', end: 'end' }, reason: null };
    if (i === 0)
      return { ...row, how: { kind: 'end', end: 'start' }, reason: null };
    const ahead = cornerAt(segs, i);
    if (ahead?.axes.some((x) => x.along === i))
      return {
        ...row,
        how: { kind: 'corner', cornerIndex: i, along: i },
        reason: null,
      };
    const behind = cornerAt(segs, i - 1);
    if (behind?.axes.some((x) => x.along === i))
      return {
        ...row,
        how: { kind: 'corner', cornerIndex: i - 1, along: i },
        reason: null,
      };
    const held = [ahead, behind].filter(Boolean);
    return {
      ...row,
      how: null,
      reason: held.every((c) => c.reason === WALLS_MEET_REASON)
        ? WALLS_MEET_REASON
        : OFF_WALL_REASON,
    };
  });
}

/**
 * Set section `index` to `lengthM` metres — the run end it reaches moves (held
 * where the end handle would hold it), else the corner whose far side slides.
 *
 * @returns {{ stored: Object, moves: Array } | { refused: string }}
 */
export function sectionLengthEdit(stored, room, index, lengthM, openings = []) {
  const row = runSectionLengths(stored, room)[index];
  if (!row) return { refused: `There is no section ${index + 1}.` };
  if (!row.how) return { refused: row.reason };
  if (!(lengthM > 0)) return { refused: 'A section needs a length.' };
  if (row.how.kind === 'corner') {
    return cornerMoveEdit(
      stored,
      room,
      row.how.cornerIndex,
      row.how.along,
      lengthM - row.lengthM
    );
  }
  const run = rehydrateRun(stored, room);
  const end = row.how.end;
  const at = runEndSegment(run, end);
  if (!at) return { refused: 'This run has a section the plan cannot place.' };
  const { startParam: s, endParam: e } = at.frame;
  const sign = Math.sign(e - s) || 1;
  const wanted = end === 'end' ? s + sign * lengthM : e - sign * lengthM;
  const param = clampRunEnd(run, end, wanted, {
    room,
    stored,
    neighbours: runNeighbourSpans(room, run),
    openings,
  });
  const edited = runEndEdit(stored, run, end, param);
  return edited
    ? { stored: edited, moves: [] }
    : { refused: 'This run end cannot move.' };
}

/**
 * A typed section length as a run edit (`{ kind: 'section-length' }` on the
 * run-edit channel): the room's objects and runs after the edit, re-solved,
 * with the appliances on every island section that slid carried along — the
 * fill seats none on a free-standing section (the corner drag carries them
 * the same way). Null where the edit is refused.
 *
 * @returns {{ objects: Object[], cabinetRuns: Object[] } | null}
 */
export function applySectionLength(room, runId, index, lengthM, openings = []) {
  const stored = (room?.cabinetRuns || []).find((r) => r.id === runId);
  if (!stored) return null;
  const out = sectionLengthEdit(stored, room, index, lengthM, openings);
  if (!out.stored) return null;
  const carried = out.moves.reduce(
    (objects, m) =>
      translateFreeSegmentAppliances(objects, out.stored, m.segIndex, m.delta)
        .objects,
    room.objects || []
  );
  const resolve = materializeRunObjects(
    rehydrateRun(out.stored, room),
    room,
    openings
  );
  return {
    objects: objectsAfterRunResolve(
      { ...room, objects: carried },
      runId,
      resolve.objects,
      resolve
    ),
    cabinetRuns: room.cabinetRuns.map((r) => (r.id === runId ? out.stored : r)),
  };
}
